import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal } from "reactstrap";
import { InputField } from "../../Component/Common/InputField";
import { castandProfessionValidation } from "../../helper/errorSchema";
import { checkFormError } from "../../helper";
import { addCastPro, getCastProfession, updateCastPro } from "../../services/serviceCastPro";

const CAST_FIELDS = [
  { name: "caste_english", label: "Caste English" },
  { name: "caste_hindi", label: "Caste Hindi" },
  { name: "profession_english", label: "Profession English" },
  { name: "profession_hindi", label: "Profession Hindi" },
  { name: "assembly_number", label: "Assembly Number" },
];

// The "Add Cast And Profession" modal from CastPro.js, moved to its own
// routed page - also handles Edit (reached via CastList's edit action as
// /cast-management/add?editId=<uuid>). There's no single-record fetch
// endpoint for Cast & Profession (confirmed - serviceCastPro.js only has a
// list endpoint), so edit mode fetches the list and finds the matching
// record client-side, same as the list already does for its own display.
export const AddCast = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("editId");

  const [isEdit, setIsEdit] = useState(!!editId);
  const [inputValue, setInputValue] = useState({
    caste_english: "",
    caste_hindi: "",
    profession_english: "",
    profession_hindi: "",
    assembly_number: "",
  });
  const [inputError, setInputError] = useState({});
  const [isLoadding, setIsLoadding] = useState(false);

  const goBackToList = () => navigate("/cast-management");

  useEffect(() => {
    if (!editId) return;
    const loadRecord = async () => {
      const res = await getCastProfession({ limit: 1000 });
      const record = (res?.results || []).find((item) => item.uuid === editId);
      if (record) {
        setInputValue({
          id: record.uuid,
          caste_english: record.caste_english,
          caste_hindi: record.caste_hindi,
          profession_english: record.profession_english,
          profession_hindi: record.profession_hindi,
          assembly_number: record.assembly_number,
        });
        setIsEdit(true);
      }
    };
    loadRecord();
  }, [editId]);

  const handleOnChange = async (name, value) => {
    const stateObj = { ...inputValue, [name]: value };
    setInputValue(stateObj);
    const error = await checkFormError(stateObj, castandProfessionValidation);
    setInputError(error);
  };

  const onSuccessResponse = (message) => {
    toast.success(message?.message || "success");
    queryClient.refetchQueries("getCastProfession");
    goBackToList();
  };

  const addMutation = useMutation(addCastPro, {
    onSuccess: (res) => {
      setIsLoadding(false);
      if (res?.success) onSuccessResponse(res);
    },
  });

  const updateMutation = useMutation(updateCastPro, {
    onSuccess: (res) => {
      setIsLoadding(false);
      if (res?.success) onSuccessResponse(res);
    },
  });

  const onSubmit = async () => {
    const validationResult = await checkFormError(
      {
        caste_english: inputValue.caste_english,
        caste_hindi: inputValue.caste_hindi,
        profession_english: inputValue.profession_english,
        profession_hindi: inputValue.profession_hindi,
        assembly_number: inputValue.assembly_number,
      },
      castandProfessionValidation
    );
    if (validationResult === true) {
      setIsLoadding(true);
      const payload = {
        id: inputValue.id,
        caste_english: inputValue.caste_english,
        caste_hindi: inputValue.caste_hindi,
        profession_english: inputValue.profession_english,
        profession_hindi: inputValue.profession_hindi,
        assembly_number: inputValue.assembly_number,
      };
      if (isEdit) {
        updateMutation.mutate(payload);
      } else {
        addMutation.mutate(payload);
      }
    } else {
      setIsLoadding(false);
      setInputError(validationResult);
    }
  };

  const requiredErrorCount = CAST_FIELDS.filter((field) => !!inputError?.[field.name]).length;

  return (
    <Modal isOpen toggle={goBackToList} centered className="avf-dialog" contentClassName="avf-content">
      <div className="avf-header">
        <div className="avf-header-top">
          <div>
            <h2 className="avf-title">{isEdit ? "Edit Cast And Profession" : "Add Cast And Profession"}</h2>
            <p className="avf-subtitle">Fill in the caste and profession details below</p>
          </div>
          <button type="button" className="avf-close-btn" onClick={goBackToList} aria-label="Close">
            ×
          </button>
        </div>
      </div>

      <div className="avf-body">
        <div className="avf-section">
          <div className="avf-section-head">
            <span className="avf-section-num">1</span>
            <h3 className="avf-section-title">Cast &amp; Profession Details</h3>
          </div>
          <div className="avf-grid">
            {CAST_FIELDS.map((field) => (
              <div className="avf-field" key={field.name}>
                <InputField
                  floating
                  className="modal_input"
                  label={field.label}
                  labelClass="modal_label"
                  labelSpan="*"
                  labelSpanClass="style_for_star"
                  placeholder={field.label}
                  name={field.name}
                  focus={!!inputError?.[field.name]}
                  error={inputError?.[field.name]}
                  value={inputValue?.[field.name]}
                  onChange={({ target: { name, value } }) => handleOnChange(name, value)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="avf-footer">
        <div className={`avf-footer-note ${requiredErrorCount > 0 ? "has-errors" : ""}`}>
          {requiredErrorCount > 0
            ? `${requiredErrorCount} of ${CAST_FIELDS.length} required fields need attention`
            : `${CAST_FIELDS.length} required fields`}
        </div>
        <div className="avf-footer-actions">
          <button type="button" className="avf-btn avf-btn-secondary" onClick={goBackToList}>
            Cancel
          </button>
          {isLoadding ? (
            <button type="button" className="avf-btn avf-btn-primary" disabled>
              Processing...
            </button>
          ) : (
            <button type="button" className="avf-btn avf-btn-primary" onClick={onSubmit}>
              {isEdit ? "Update" : "Add"}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};
